import {
  DeviceOperationalState,
  DeviceOperationalStatePoint,
  MetricUnit,
} from '@/__generated__/graphql';
import { type Formatters } from '@/hooks/useFormatters';
import { getDeviceOperationalStateAttributes } from '@/utils/attributes/deviceOperationalState';
import COLORS from '@features/controlCenter/constants/colors.json';
import { type I18n } from '@lingui/core';
import { msg, t } from '@lingui/macro';
import { CustomSeriesRenderItemReturn } from 'echarts';
import EChartsOptionBuilder, { SerieType } from './EChartsOptionBuilder';

type BasicAlert = {
  timestamp: number;
  min?: number | null;
  max?: number | null;
  overflow?: number | null;
};

export const flowAlertsSeries = ({
  namePrefix,
  flow,
  distributionMaxFlow,
  unit,
  precision,
  xAxisId,
  yAxisId,
  i18n,
  formatters,
}: {
  namePrefix: string | undefined | null;
  flow: BasicAlert[] | undefined | null;
  distributionMaxFlow: number | undefined | null;
  unit: MetricUnit;
  precision: number;
  xAxisId: string | undefined;
  yAxisId: string;
  i18n: I18n;
  formatters: Formatters;
}): Parameters<EChartsOptionBuilder['addSeries']>[0] => {
  const { formatDateTime, formatMetricValue } = formatters;
  const series: Parameters<EChartsOptionBuilder['addSeries']>[0] = [];

  if (flow != null) {
    const flowIntervals: [number, number, number | null, number | null, number | null][] = [];
    for (let i = 0; i < flow.length; i++) {
      const currentFlow = flow[i];
      const nextTimestamp = flow[i + 1]?.timestamp ?? Date.now();
      flowIntervals.push([
        currentFlow.timestamp,
        nextTimestamp,
        currentFlow.min ?? null,
        currentFlow.max ?? null,
        currentFlow.overflow ?? null,
      ]);
    }
    series.push({
      meta: {
        type: 'line',
        name: namePrefix ? namePrefix + t(i18n)`Alertas de caudal` : t(i18n)`Alertas de caudal`,
        data: flowIntervals,
        dataType: 'flowAlerts',
        yAxisId,
        xAxisId,
      },
      value(serieValue) {
        return {
          ...serieValue,
          type: 'custom' as const,
          z: 1000,
          clip: true,
          color: COLORS.BLUE,
          encode: {
            x: [0, 1],
            y: [2, 3, 4],
            tooltip: [2, 3, 4],
          },
          dimensions: ['timestamp', 'timestamp2', 'min', 'max', 'overflow'],
          renderItem(_, api) {
            const a1 = api.coord([api.value(0), api.value(2)]);
            const a2 = api.coord([api.value(1), api.value(2)]);
            const b1 = api.coord([api.value(0), api.value(3)]);
            const b2 = api.coord([api.value(1), api.value(3)]);
            const c1 = api.coord([api.value(0), api.value(4)]);
            const c2 = api.coord([api.value(1), api.value(4)]);
            const group: CustomSeriesRenderItemReturn = {
              type: 'group',
              focus: 'self',
              children: [
                {
                  type: 'line',
                  shape: {
                    x1: a1[0],
                    x2: a2[0],
                    y1: a1[1],
                    y2: a2[1],
                  },
                  style: {
                    lineDash: 'dashed',
                    stroke: COLORS.BLUE,
                    lineWidth: 2,
                    opacity: 0.7,
                  },
                },
                {
                  type: 'line',
                  shape: {
                    x1: b1[0],
                    x2: b2[0],
                    y1: b1[1],
                    y2: b2[1],
                  },
                  style: {
                    lineDash: 'dashed',
                    stroke: COLORS.YELLOW,
                    lineWidth: 2,
                    opacity: 0.7,
                  },
                },
                {
                  type: 'line',
                  shape: {
                    x1: c1[0],
                    x2: c2[0],
                    y1: c1[1],
                    y2: c2[1],
                  },
                  style: {
                    lineDash: 'dashed',
                    stroke: COLORS.RED,
                    lineWidth: 2,
                    opacity: 0.7,
                  },
                },
              ],
            };
            return group;
          },
          tooltip: {
            show: true,
            trigger: 'item',
            formatter(params) {
              const [from, to, min, max, overflow] = params.value as [
                number,
                number,
                number | null,
                number | null,
                number | null,
              ];
              const fromString = formatDateTime(from);
              const toString = formatDateTime(to);
              const opt = { unit, precision };
              const minString = min != null ? formatMetricValue(min, opt) : '-';
              const maxString = max != null ? formatMetricValue(max, opt) : '-';
              const overflowString = overflow != null ? formatMetricValue(overflow, opt) : '-';

              return `<b>${params.seriesName}</b></br>
${fromString} - ${toString}
<br>${t(i18n)`Caudal bajo`}: <b>${minString}</b>
<br>${t(i18n)`Caudal alto`}: <b>${maxString}</b>
<br>${t(i18n)`Caudal desborde`}: <b>${overflowString}</b>`;
            },
          },
        } as SerieType;
      },
    });
  }

  if (distributionMaxFlow != null)
    series.push({
      meta: {
        type: 'scatter' as const,
        name: namePrefix
          ? namePrefix + t(i18n)`Caudal máximo de reparto`
          : t(i18n)`Caudal máximo de reparto`,
        data: [[Date.now(), distributionMaxFlow]],
        dataType: 'distributionMaxFlow',
        yAxisId,
        color: COLORS.ORANGE,
      },
      value(serieValue) {
        return {
          ...serieValue,
          symbolSize: 0,
          markLine: {
            data: [
              {
                yAxis: distributionMaxFlow,
                lineStyle: {
                  color: COLORS.ORANGE,
                },
              },
            ],
            symbol: 'none',
            label: {
              show: false,
              position: 'middle',
              color: 'white',
              fontWeight: 'bold',
              formatter: (params) => formatMetricValue(params.value as number, { unit, precision }),
            },
            emphasis: {
              label: {
                show: true,
              },
            },
          },
        };
      },
    });
  return series;
};

export const levelAlertsSeries = ({
  namePrefix,
  level,
  ratingCurvesMin,
  ratingCurvesMax,
  unit,
  precision,
  yAxisId,
  xAxisId,
  i18n,
  formatters,
}: {
  namePrefix: string | undefined | null;
  level: BasicAlert[] | undefined | null;
  ratingCurvesMin: number | undefined | null;
  ratingCurvesMax: number | undefined | null;
  unit: MetricUnit;
  precision: number;
  yAxisId: string;
  xAxisId: string | undefined;
  i18n: I18n;
  formatters: Formatters;
}): Parameters<EChartsOptionBuilder['addSeries']>[0] => {
  const { formatDateTime, formatMetricValue } = formatters;
  const series: Parameters<EChartsOptionBuilder['addSeries']>[0] = [];
  if (level != null) {
    const levelIntervals: [number, number, number | null, number | null, number | null][] = [];
    for (let i = 0; i < level.length; i++) {
      const currentLevel = level[i];
      const nextTimestamp = level[i + 1]?.timestamp ?? Date.now();
      levelIntervals.push([
        currentLevel.timestamp,
        nextTimestamp,
        currentLevel.min ?? null,
        currentLevel.max ?? null,
        currentLevel.overflow ?? null,
      ]);
    }
    series.push({
      meta: {
        type: 'line',
        name: namePrefix ? namePrefix + t(i18n)`Alertas de nivel` : t(i18n)`Alertas de nivel`,
        data: levelIntervals,
        dataType: 'levelAlerts',
        yAxisId,
        xAxisId,
      },
      value(serieValue) {
        return {
          ...serieValue,
          color: COLORS.GREEN,
          type: 'custom' as const,
          z: 1000,
          clip: true,
          encode: {
            x: [0, 1],
            y: [2, 3, 4],
            tooltip: [2, 3, 4],
          },
          dimensions: ['timestamp', 'timestamp2', 'min', 'max', 'overflow'],
          renderItem(_, api) {
            const a1 = api.coord([api.value(0), api.value(2)]);
            const a2 = api.coord([api.value(1), api.value(2)]);
            const b1 = api.coord([api.value(0), api.value(3)]);
            const b2 = api.coord([api.value(1), api.value(3)]);
            const c1 = api.coord([api.value(0), api.value(4)]);
            const c2 = api.coord([api.value(1), api.value(4)]);
            const group: CustomSeriesRenderItemReturn = {
              type: 'group',
              focus: 'self',
              children: [
                {
                  type: 'line',
                  shape: {
                    x1: a1[0],
                    x2: a2[0],
                    y1: a1[1],
                    y2: a2[1],
                  },
                  style: {
                    lineDash: 'dashed',
                    stroke: COLORS.BLUE,
                    lineWidth: 2,
                    opacity: 0.7,
                  },
                },
                {
                  type: 'line',
                  shape: {
                    x1: b1[0],
                    x2: b2[0],
                    y1: b1[1],
                    y2: b2[1],
                  },
                  style: {
                    lineDash: 'dashed',
                    stroke: COLORS.YELLOW,
                    lineWidth: 2,
                    opacity: 0.7,
                  },
                },
                {
                  type: 'line',
                  shape: {
                    x1: c1[0],
                    x2: c2[0],
                    y1: c1[1],
                    y2: c2[1],
                  },
                  style: {
                    lineDash: 'dashed',
                    stroke: COLORS.RED,
                    lineWidth: 2,
                    opacity: 0.7,
                  },
                },
              ],
            };
            return group;
          },
          tooltip: {
            show: true,
            trigger: 'item',
            formatter(params) {
              const [from, to, min, max, overflow] = params.value as [
                number,
                number,
                number | null,
                number | null,
                number | null,
              ];
              const fromString = formatDateTime(from);
              const toString = formatDateTime(to);
              const opt = { unit, precision };
              const minString = min != null ? formatMetricValue(min, opt) : '-';
              const maxString = max != null ? formatMetricValue(max, opt) : '-';
              const overflowString = overflow != null ? formatMetricValue(overflow, opt) : '-';

              return `<b>${params.seriesName}</b></br>
${fromString} - ${toString}
<br>${t(i18n)`Nivel bajo`}: <b>${minString}</b>
<br>${t(i18n)`Nivel alto`}: <b>${maxString}</b>
<br>${t(i18n)`Nivel desborde`}: <b>${overflowString}</b>`;
            },
          },
        } as SerieType;
      },
    });
  }

  if (ratingCurvesMax != null || ratingCurvesMin != null)
    series.push({
      meta: {
        type: 'scatter' as const,
        name: namePrefix
          ? namePrefix + t(i18n)`Límites de curvas de aforo`
          : t(i18n)`Límites de curvas de aforo`,
        data: [
          ...(ratingCurvesMax != null ? [[Date.now(), ratingCurvesMax]] : []),
          ...(ratingCurvesMin != null ? [[Date.now(), ratingCurvesMin]] : []),
        ],
        dataType: 'ratingCurves',
        yAxisId,
      },
      value(serieValue) {
        return {
          ...serieValue,
          symbolSize: 0,
          markLine: {
            data: [
              ...(ratingCurvesMax != null
                ? [
                    {
                      yAxis: ratingCurvesMax,
                      name: namePrefix
                        ? namePrefix + t(i18n)`Límite superior de curvas de aforo`
                        : t(i18n)`Límite superior de curvas de aforo`,
                      lineStyle: {
                        color: COLORS.RED,
                      },
                    },
                  ]
                : []),
              ...(ratingCurvesMin != null
                ? [
                    {
                      yAxis: ratingCurvesMin,
                      name: namePrefix
                        ? namePrefix + t(i18n)`Límite inferior de curvas de aforo`
                        : t(i18n)`Límite inferior de curvas de aforo`,
                      lineStyle: {
                        color: COLORS.YELLOW,
                      },
                    },
                  ]
                : []),
            ],
            symbol: 'none',
            label: {
              show: false,
              position: 'middle',
              color: 'white',
              fontWeight: 'bold',
              formatter: (params) =>
                `${params.seriesName}: ${formatMetricValue(params.value as number, {
                  unit,
                  precision,
                })}`,
            },
            emphasis: {
              label: {
                show: true,
              },
            },
          },
        };
      },
    });

  return series;
};

export const connectionStateMarkAreaSerie = ({
  serieName,
  connectionState,
  yAxisId,
  xAxisId,
  i18n,
}: {
  serieName: string;
  connectionState: [number, number][];
  yAxisId: string;
  xAxisId?: string;
  i18n: I18n;
}): Parameters<EChartsOptionBuilder['addSeries']>[0][number] => ({
  meta: {
    type: 'scatter',
    name: serieName,
    dataType: 'connectionState',
    yAxisId,
    xAxisId,
  },
  value(serieValue) {
    const connectionStateMarkAreaData = connectionState;
    const serieData = connectionStateMarkAreaData?.map(([timestamp, value]) => [
      timestamp,
      value === 1 ? i18n._(msg`conectado`) : i18n._(msg`desconectado`),
    ]) as [number, string][];
    return {
      ...serieValue,
      data: serieData,
      z: -1,
      markArea: {
        data: connectionStateMarkAreaData.reduce(
          (acc, current, index) => {
            if (index === connectionStateMarkAreaData.length - 1) {
              const lastIndex = connectionStateMarkAreaData.length - 1;
              if (connectionStateMarkAreaData[lastIndex][1] === 1) {
                acc.push([
                  {
                    xAxis: connectionStateMarkAreaData[lastIndex][0],
                  },
                  {
                    xAxis: Date.now(),
                  },
                ]);
              }
            } else if (current[1] === 1) {
              acc.push([
                {
                  xAxis: current[0],
                },
                {
                  xAxis: connectionStateMarkAreaData[index + 1][0],
                },
              ]);
            }
            return acc;
          },
          [] as NonNullable<echarts.MarkAreaComponentOption['data']>,
        ),
      },
    };
  },
});

export const operationalStatesMarkArea = ({
  operationalStates,
  yAxisId,
  xAxisId,
  i18n,
}: {
  operationalStates: DeviceOperationalStatePoint[];
  yAxisId: string;
  xAxisId?: string;
  i18n: I18n;
}): Parameters<EChartsOptionBuilder['addSeries']>[0][number][] => {
  const operationalStatesWithStops = operationalStates.map((point, idx, array) => {
    const nextPoint = array.at(idx + 1);
    return {
      value: point.value,
      start: point.timestamp,
      stop: nextPoint?.timestamp ?? Date.now(),
    };
  });

  const statesData = Object.values(DeviceOperationalState).map((state) => {
    const attributes = getDeviceOperationalStateAttributes(state, i18n);
    return {
      state,
      label: attributes.label,
      color: attributes.markAreaColor,
      data: operationalStatesWithStops.filter((point) => point.value === state),
    };
  });

  const data: Parameters<EChartsOptionBuilder['addSeries']>[0][number][] = statesData.map(
    ({ label, data, color }) => ({
      meta: {
        type: 'scatter',
        name: label,
        dataType: 'operationalStates',
        yAxisId,
        xAxisId,
        color,
      },
      value(serieValue) {
        const serieData = data.map(({ start, stop }) => {
          return [start, label, stop] as [number, string, number];
        });
        return {
          ...serieValue,
          symbol: 'rectangle',
          data: serieData,
          z: -1,
          markArea: {
            data: serieData.map(([start, _, stop]) => [
              {
                xAxis: start,
                itemStyle: { color: color },
              },
              {
                xAxis: stop,
              },
            ]),
          },
        };
      },
    }),
  );
  return data;
};
